/**
 * useIdentity — IDENTITY:P3.A Composite Identity Hook
 *
 * Merges alias + avatar + rank state into a single identity object.
 * Accepts `user` parameter (prop-drilled from App.jsx) to determine auth state.
 * When no user is provided, returns null identity without making network requests.
 *
 * Solution Proposal: SOL-20260309-IDENTITY-P3
 */
import { useState, useCallback, useEffect } from 'react';
import { getDisplayIdentity } from '../services/identityDisplayApi';

/**
 * useIdentity — Fetch and manage composite display identity.
 * @param {Object|null} user - Current user from App.jsx prop drilling
 * @returns {{ alias, aliasLower, avatarUrl, thumbnailUrl, fallbackLetter, fallbackColor, rankTier, rankScore, tier, totalPoints, isLoading, error, refetch }}
 */
export function useIdentity(user) {
  const [identity, setIdentity] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchIdentity = useCallback(async () => {
    if (!user) {
      setIdentity(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const data = await getDisplayIdentity();
      setIdentity(data);
    } catch (err) {
      setError(err?.message || 'Failed to fetch identity');
      setIdentity(null);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchIdentity();
  }, [fetchIdentity]);

  const i = identity;
  return {
    alias: i ? i.alias : null,
    aliasLower: i ? i.alias_lower : null,
    avatarUrl: i ? i.avatar_url : null,
    thumbnailUrl: i ? i.thumbnail_url : null,
    fallbackLetter: i ? i.fallback_letter : null,
    fallbackColor: i ? i.fallback_color : null,
    rankTier: i ? i.rank_tier : null,
    rankScore: i ? (i.rank_score ?? 0) : 0,
    tier: i ? i.tier : null,
    totalPoints: i ? (i.total_points ?? 0) : 0,
    isLoading,
    error,
    refetch: fetchIdentity,
  };
}
